import { Avatar, Box, Button, Paper, Slide, Stack, Typography } from "@mui/material";
import PhoneCallbackIcon from "@mui/icons-material/PhoneCallback";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import PhoneIcon from "@mui/icons-material/Phone";

import { colors, alphaVar } from "../../../ui/colors";
import { useFloatingBubbles } from "../../../../context/floating-bubbles/FloatingBubblesContext";
import { useContactCenterTheme } from "../../theming/ContactCenterThemeContext";
import { ContactCenterThemeScope } from "../../theming/ContactCenterThemeScope";
import { useOptionalCallArbitration } from "./CallArbitrationContext";

export const ConcurrentCallChoice = () => {
  const { theme } = useContactCenterTheme();
  const { zIndex } = useFloatingBubbles();
  const arbitration = useOptionalCallArbitration();

  if (!arbitration || !arbitration.showChoice) return null;

  const {
    currentLabel,
    incomingLabel,
    answerNewEndCurrent,
    holdCurrentAnswerNew,
    hangupIncoming,
  } = arbitration;

  const incomingInitial = incomingLabel?.trim()?.at(0)?.toUpperCase() || "?";
  const isNumeric = /^[+\d]/.test(incomingLabel?.trim() || "");

  return (
    <ContactCenterThemeScope>
      <Slide direction="down" in mountOnEnter unmountOnExit>
        <Paper
          elevation={0}
          sx={{
            position: "fixed",
            top: 92,
            right: 20,
            zIndex: zIndex.callNotification,
            width: 300,
            background: alphaVar(theme.toast.bg, 0.97),
            color: theme.shell.textPrimary,
            backdropFilter: "blur(10px)",
            padding: "16px 16px 12px",
            borderRadius: 2,
            boxShadow: `0 8px 28px ${alphaVar("#000000", 0.18)}`,
            border: `1px solid ${theme.shell.border}`,
          }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
            <PhoneCallbackIcon sx={{ fontSize: "0.9rem", color: theme.shell.accent }} />
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: theme.shell.textSecondary,
              }}
            >
              Incoming call
            </Typography>
          </Stack>

          <Box sx={{ textAlign: "center", mb: 1.5 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                margin: "0 auto",
                backgroundColor: theme.shell.accent,
                color: "#fff",
                fontSize: "1.2rem",
                fontWeight: 700,
                boxShadow: `0 2px 8px ${alphaVar(theme.shell.accent, 0.3)}`,
              }}
            >
              {isNumeric ? <PhoneIcon sx={{ fontSize: "1.2rem" }} /> : incomingInitial}
            </Avatar>

            <Typography
              sx={{
                fontSize: "1rem",
                fontWeight: 700,
                color: theme.shell.textPrimary,
                mt: 1,
              }}
            >
              {incomingLabel}
            </Typography>

            <Typography sx={{ fontSize: "0.75rem", color: theme.shell.textSecondary, mt: 0.25 }}>
              On a call with{" "}
              <Box component="span" sx={{ color: theme.shell.accent, fontWeight: 600 }}>
                {currentLabel}
              </Box>
            </Typography>
          </Box>

          <Stack spacing={0.75}>
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                onClick={hangupIncoming}
                startIcon={<PhoneDisabledIcon sx={{ fontSize: "0.95rem" }} />}
                sx={{
                  color: colors.danger.base,
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  padding: "8px 12px",
                  textTransform: "none",
                  "&:hover": { backgroundColor: alphaVar(colors.danger.base, 0.05), color: colors.danger[700] },
                }}
              >
                Decline
              </Button>

              <Button
                fullWidth
                variant="contained"
                onClick={holdCurrentAnswerNew}
                startIcon={<PhoneIcon sx={{ fontSize: "0.95rem" }} />}
                sx={{
                  background: `linear-gradient(135deg, ${colors.success.base}, ${colors.success[600]})`,
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  padding: "8px 12px",
                  textTransform: "none",
                  borderRadius: 1,
                  boxShadow: `0 2px 8px ${alphaVar(colors.success.base, 0.25)}`,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    boxShadow: `0 4px 12px ${alphaVar(colors.success.base, 0.3)}`,
                    transform: "scale(1.02)",
                  },
                  "&:active": { transform: "scale(0.98)" },
                }}
              >
                Answer
              </Button>
            </Stack>

            <Button
              fullWidth
              size="small"
              onClick={answerNewEndCurrent}
              sx={{
                color: theme.shell.textSecondary,
                fontWeight: 600,
                fontSize: "0.76rem",
                textTransform: "none",
                "&:hover": { backgroundColor: alphaVar(theme.shell.textSecondary, 0.06) },
              }}
            >
              Answer &amp; end current
            </Button>
          </Stack>
        </Paper>
      </Slide>
    </ContactCenterThemeScope>
  );
};
